'use client';

// React Internationalization Context for Global PI-APP
// Following CODING_STANDARDS.md requirements for React component patterns with TypeScript interfaces

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { globalI18nService, LocaleConfig, TranslationContext } from '@/lib/global/services/GlobalInternationalizationService';
import { useAuth0 } from '@/contexts/Auth0Context';

export interface I18nContextValue {
  currentLocale: LocaleConfig;
  supportedLocales: LocaleConfig[];
  setLocale: (localeCode: string) => Promise<void>;
  translate: (key: string, context?: TranslationContext) => string;
  translatePlural: (key: string, count: number, context?: TranslationContext) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number, currency?: string) => string;
  formatDate: (date: Date, options?: Intl.DateTimeFormatOptions) => string;
  formatTime: (date: Date) => string;
  formatAddress: (address: any) => any;
  formatPhoneNumber: (phone: string, international?: boolean) => any;
  getBusinessInfo: () => any;
  getFinancialTerminology: () => Record<string, string>;
  getInsuranceTerminology: () => Record<string, string>;
  getPropertyTerminology: () => Record<string, string>;
  isLoading: boolean;
  error: string | null;
}

const InternationalizationContext = createContext<I18nContextValue | undefined>(undefined);

export interface InternationalizationProviderProps {
  children: ReactNode;
  defaultLocale?: string;
  detectUserLocale?: boolean;
}

export function InternationalizationProvider({
  children,
  defaultLocale = 'en-US',
  detectUserLocale = true
}: InternationalizationProviderProps) {
  const { user } = useAuth0();
  const [currentLocale, setCurrentLocale] = useState<LocaleConfig>(globalI18nService.getCurrentLocale());
  const [supportedLocales, setSupportedLocales] = useState<LocaleConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize i18n service
  useEffect(() => {
    const initializeI18n = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Initialize the service
        await globalI18nService.initialize();

        // Get supported locales
        const locales = globalI18nService.getSupportedLocales();
        setSupportedLocales(locales);

        // Determine initial locale
        let initialLocale = defaultLocale;
        
        if (detectUserLocale) {
          // Try to get user's preferred locale from their profile
          if (user?.locale) {
            initialLocale = user.locale;
          } else {
            // Detect from browser
            initialLocale = globalI18nService.detectUserLocale();
          }
        }

        // Set the initial locale
        await globalI18nService.setLocale(initialLocale, {
          userId: user?.sub,
          region: getCurrentRegionFromLocale(initialLocale)
        });

        setCurrentLocale(globalI18nService.getCurrentLocale());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize internationalization');
        console.error('I18n initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeI18n();
  }, [user, defaultLocale, detectUserLocale]);

  // Listen for locale changes
  useEffect(() => {
    const handleLocaleChange = (data: any) => {
      if (data?.current) {
        setCurrentLocale(data.current);
      }
    };

    globalI18nService.on('locale:changed', handleLocaleChange);
    
    return () => {
      globalI18nService.off('locale:changed', handleLocaleChange);
    };
  }, []);

  // Set locale function
  const setLocale = useCallback(async (localeCode: string) => {
    try {
      setError(null);
      await globalI18nService.setLocale(localeCode, {
        userId: user?.sub,
        region: getCurrentRegionFromLocale(localeCode)
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set locale');
      throw err;
    }
  }, [user]);

  // Translation functions with context
  const translate = useCallback((key: string, context?: TranslationContext) => {
    return globalI18nService.translate(key, {
      userId: user?.sub,
      region: currentLocale.region,
      ...context
    });
  }, [user, currentLocale]);

  const translatePlural = useCallback((key: string, count: number, context?: TranslationContext) => {
    return globalI18nService.translatePlural(key, count, {
      userId: user?.sub,
      region: currentLocale.region,
      ...context
    });
  }, [user, currentLocale]);

  // Formatting functions
  const formatNumber = useCallback((value: number, options?: Intl.NumberFormatOptions) => {
    return globalI18nService.formatNumber(value, { locale: currentLocale.code, ...options });
  }, [currentLocale]);

  const formatCurrency = useCallback((value: number, currency?: string) => {
    return globalI18nService.formatCurrency(value, currency as any);
  }, []);

  const formatDate = useCallback((date: Date, options?: Intl.DateTimeFormatOptions) => {
    return globalI18nService.formatDate(date, options);
  }, []);

  const formatTime = useCallback((date: Date) => {
    return globalI18nService.formatTime(date);
  }, []);

  const formatAddress = useCallback((address: any) => {
    return globalI18nService.formatAddress(address);
  }, []);

  const formatPhoneNumber = useCallback((phone: string, international: boolean = false) => {
    return globalI18nService.formatPhoneNumber(phone, international);
  }, []);

  // Regional information functions
  const getBusinessInfo = useCallback(() => {
    return globalI18nService.getBusinessInfo();
  }, []);

  const getFinancialTerminology = useCallback(() => {
    return globalI18nService.getFinancialTerminology();
  }, []);

  const getInsuranceTerminology = useCallback(() => {
    return globalI18nService.getInsuranceTerminology();
  }, []);

  const getPropertyTerminology = useCallback(() => {
    return globalI18nService.getPropertyTerminology();
  }, []);

  const contextValue: I18nContextValue = {
    currentLocale,
    supportedLocales,
    setLocale,
    translate,
    translatePlural,
    formatNumber,
    formatCurrency,
    formatDate,
    formatTime,
    formatAddress,
    formatPhoneNumber,
    getBusinessInfo,
    getFinancialTerminology,
    getInsuranceTerminology,
    getPropertyTerminology,
    isLoading,
    error
  };

  return (
    <InternationalizationContext.Provider value={contextValue}>
      {children}
    </InternationalizationContext.Provider>
  );
}

// Hook to use internationalization context
export function useInternationalization(): I18nContextValue {
  const context = useContext(InternationalizationContext);
  if (context === undefined) {
    throw new Error('useInternationalization must be used within an InternationalizationProvider');
  }
  return context;
}

// Convenience hooks for common operations
export function useTranslation() {
  const { translate, translatePlural } = useInternationalization();
  return {
    t: translate,
    tPlural: translatePlural
  };
}

export function useFormatting() {
  const { 
    formatNumber, 
    formatCurrency, 
    formatDate, 
    formatTime, 
    formatAddress, 
    formatPhoneNumber 
  } = useInternationalization();
  
  return {
    formatNumber,
    formatCurrency,
    formatDate,
    formatTime,
    formatAddress,
    formatPhoneNumber
  };
}

export function useRegionalInfo() {
  const { 
    getBusinessInfo, 
    getFinancialTerminology, 
    getInsuranceTerminology, 
    getPropertyTerminology 
  } = useInternationalization();
  
  return {
    getBusinessInfo,
    getFinancialTerminology,
    getInsuranceTerminology,
    getPropertyTerminology
  };
}

export function useLocaleSelection() {
  const { currentLocale, supportedLocales, setLocale } = useInternationalization();
  return {
    currentLocale,
    supportedLocales,
    setLocale
  };
}

// Helper function to determine region from locale
function getCurrentRegionFromLocale(localeCode: string): any {
  const regionMap: Record<string, string> = {
    'en-US': 'US',
    'en-GB': 'UK',
    'en-AU': 'AU',
    'hi-IN': 'IN',
    'de-DE': 'EU',
    'fr-FR': 'EU',
    'es-ES': 'EU',
    'it-IT': 'EU',
    'pt-PT': 'EU',
    'nl-NL': 'EU'
  };
  
  return regionMap[localeCode] || 'US';
}

export default InternationalizationContext;